import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import { apiPost } from '../config/apiClient';

function StarRating({ rating, onRate }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1 justify-center">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= (hovered || rating);
        return (
          <button
            key={star}
            type="button"
            onClick={() => onRate(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="p-1 transition-transform hover:scale-110 active:scale-95"
            aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
          >
            <svg
              className={`w-11 h-11 transition-colors ${filled ? 'text-red-700' : 'text-gray-300'}`}
              fill={filled ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={filled ? 0 : 1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

export default function UploadReceiptPage() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedReceiptId, setUploadedReceiptId] = useState(null);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [serviceRating, setServiceRating] = useState(0);
  const [serviceComment, setServiceComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, authToken, fetchUserDetailsAndPoints } = useAuth();

  useEffect(() => {
    return () => {
      if (xhrRef.current) {
        xhrRef.current.abort();
      }
    };
  }, []);

  const navigateToProfile = (receiptId, message) => {
    navigate('/profile', {
      state: {
        newReceiptId: receiptId,
        uploadMessage: message || 'Receipt uploaded successfully!'
      }
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setError('');
    setSuccessMessage('');

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setError('');
    setSuccessMessage('');
    setLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('receipt', selectedFile);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 95);
        setUploadProgress(pct);
      }
    });

    xhr.addEventListener('load', () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress(100);
          setSuccessMessage(data.message || 'Receipt uploaded successfully!');
          fetchUserDetailsAndPoints(authToken);
          setUploadedReceiptId(data.data?.id);
          setShowRatingForm(true);
          setSelectedFile(null);
          setPreview(null);
        } else {
          setUploadProgress(0);
          if (data.errors && data.errors.receipt) {
            setError(data.errors.receipt.join(', '));
          } else {
            setError(data.message || 'Upload failed. Please try again.');
          }
        }
      } catch {
        setUploadProgress(0);
        setError('Upload failed. Unexpected server response.');
      }
      setLoading(false);
    });

    xhr.addEventListener('error', () => {
      console.error('Upload error (XHR)');
      setUploadProgress(0);
      setError('Upload failed. Check your connection and try again.');
      setLoading(false);
    });

    xhr.addEventListener('abort', () => {
      setUploadProgress(0);
      setError('Upload was cancelled.');
      setLoading(false);
    });

    xhr.open('POST', `${API_BASE_URL}/receipts/upload-centralized`);
    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.send(formData);
  };

  const handleRatingSubmit = async () => {
    if (serviceRating === 0) {
      setRatingError('Please select a star rating.');
      return;
    }

    setRatingSubmitting(true);
    setRatingError('');

    try {
      await apiPost(`/receipts/${uploadedReceiptId}/rate`, {
        service_rating: serviceRating,
        service_comment: serviceComment.trim() || null,
      });
      navigateToProfile(uploadedReceiptId, successMessage);
    } catch (err) {
      console.error('Rating submit error:', err);
      setRatingError('Could not submit rating. Please try again.');
      setRatingSubmitting(false);
    }
  };

  const handleSkipRating = () => {
    navigateToProfile(uploadedReceiptId, successMessage);
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Sign in to upload receipts</h2>
        <p className="text-gray-500 text-sm mb-6">Share a receipt and help your community find better prices!</p>
        <Link to="/login" className="inline-block bg-red-800 text-white font-semibold px-6 py-3 rounded-lg hover:bg-red-900 transition">
          Log In
        </Link>
      </div>
    );
  }

  if (showRatingForm) {
    const ratingLabels = ['', 'Terrible', 'Poor', 'Okay', 'Good', 'Excellent'];
    return (
      <div className="max-w-lg mx-auto px-4 py-8 pb-24 md:pb-10">
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            {successMessage}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-gray-800 text-center mb-1">Rate Your Experience</h2>
          <p className="text-sm text-gray-500 text-center mb-6">How was the service at this store?</p>

          <StarRating rating={serviceRating} onRate={setServiceRating} />

          {serviceRating > 0 && (
            <p className="text-center text-sm font-medium text-red-700 mt-2">
              {ratingLabels[serviceRating]}
            </p>
          )}

          <div className="mt-5">
            <label htmlFor="service-comment" className="block text-sm font-medium text-gray-700 mb-1">
              Comments <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="service-comment"
              rows={3}
              maxLength={500}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-700 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none resize-none transition"
              placeholder="How was your shopping experience?"
              value={serviceComment}
              onChange={(e) => setServiceComment(e.target.value)}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{serviceComment.length}/500</p>
          </div>

          {ratingError && (
            <p className="text-red-600 text-sm mt-3 text-center">{ratingError}</p>
          )}

          <button
            onClick={handleRatingSubmit}
            disabled={ratingSubmitting || serviceRating === 0}
            className="w-full mt-4 bg-red-800 text-white px-4 py-3.5 rounded-lg hover:bg-red-900 transition duration-150 font-semibold disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
          >
            {ratingSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Submitting...
              </span>
            ) : 'Submit Rating'}
          </button>

          <button
            onClick={handleSkipRating}
            disabled={ratingSubmitting}
            className="w-full mt-2 text-gray-500 hover:text-gray-700 text-sm py-2 transition"
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8 pb-24 md:pb-10">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">Upload Receipt</h1>
      <p className="mb-6 text-sm text-gray-500">Snap a photo of your supermarket receipt. We'll extract prices and help everyone find better deals.</p>

      {error && (
        <div className="mb-4 flex items-start gap-2 text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span>{error}</span>
        </div>
      )}

      {loading && uploadProgress > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-red-800 h-2 rounded-full transition-all duration-500 ease-out" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {!selectedFile ? (
          <div 
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-red-400 hover:bg-red-50/50 transition cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-3">
              <svg className="w-7 h-7 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <p className="font-semibold text-gray-700 mb-1">Take photo or choose file</p>
            <p className="text-xs text-gray-400">JPG, PNG, or PDF up to 10MB</p>
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/jpeg,image/png,application/pdf"
              capture="environment"
              onChange={handleFileChange} 
              className="hidden"
              disabled={loading}
            />
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl p-4 bg-white">
            {preview && (
              <img src={preview} alt="Receipt preview" className="w-full max-h-64 object-contain rounded-lg mb-3 bg-gray-50" />
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="text-sm text-gray-700 truncate">{selectedFile.name}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">({(selectedFile.size / 1024).toFixed(0)} KB)</span>
              </div>
              {!loading && (
                <button type="button" onClick={handleRemoveFile} className="text-gray-400 hover:text-red-500 transition p-1">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          </div>
        )}

        <button 
          type="submit" 
          className="w-full mt-4 bg-red-800 text-white px-4 py-3.5 rounded-lg hover:bg-red-900 transition duration-150 font-semibold disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
          disabled={loading || !selectedFile}
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Uploading...
            </span>
          ) : 'Upload Receipt'}
        </button>
      </form>

      <div className="mt-8 bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-700 text-sm mb-2">What happens next?</h3>
        <ol className="text-xs text-gray-500 space-y-1.5 list-decimal list-inside">
          <li>We extract product names and prices from your receipt</li>
          <li>Prices are compared against other stores in your area</li>
          <li>You unlock more searches for contributing</li>
        </ol>
      </div>
    </div>
  );
}
